// Apex Business Intelligence — Round 12 additive components
//   - SaveNoteButton           (Change 1)
//   - BackButton               (Change 2)
//   - MentionEmailDropdown     (Change 3)
//   - CustomerPopout           (Change 6)
//   - JobSectionPercentages    (Change 7)
//   - AvgJobSizePage           (Change 8)
//   - TotalInvoicesCard        (Change 10)
//   - PnLPage                  (Change 11)
//   - SpecialPermissionsPage   (Change 12)
import React, { useState, useEffect, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "apex_auth_token";
const _h = () => { const t = localStorage.getItem(TOKEN_KEY); return t ? { Authorization: "Bearer " + t } : {}; };
const j = (path, opts = {}) =>
  fetch(`${API}${path}`, { ...opts, headers: { "Content-Type": "application/json", ..._h(), ...(opts.headers || {}) } })
    .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw Object.assign(new Error(d.detail || r.statusText), { status: r.status }); return d; });

const card = { background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 8, padding: 16, marginBottom: 12 };
const subhead = { fontFamily: "var(--rajdhani)", fontSize: 14, fontWeight: 700, color: "#2C2C2C", marginBottom: 10, letterSpacing: 0.5 };
const labelDark = { fontSize: 11, color: "#2C2C2C", fontWeight: 600, marginBottom: 4, display: "block" };

// ═══ Change 1 — Save Note Button ════════════════════════════════════════════
export function SaveNoteButton({ onSave, lastSavedAt, label = "Save Note", testid = "save-note-btn" }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const click = async () => {
    setBusy(true); setDone(false);
    try { await Promise.resolve(onSave && onSave()); setDone(true); setTimeout(() => setDone(false), 2000); }
    catch (e) { alert("Save failed: " + e.message); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
      <button data-testid={testid} className="btn btn-primary" onClick={click} disabled={busy} style={{ fontSize: 11 }}>
        {busy ? "Saving…" : done ? "✓ Saved" : label}
      </button>
      {lastSavedAt && <span style={{ fontSize: 10, color: "#2C2C2C", fontWeight: 600 }}>Auto-saved {String(lastSavedAt).slice(11, 19)}</span>}
    </div>
  );
}

// ═══ Change 2 — Universal Back Button ══════════════════════════════════════
export function BackButton({ onBack, label = "← Back", testid = "back-btn" }) {
  const go = () => { if (onBack) return onBack(); if (window.history.length > 1) window.history.back(); else window.location.hash = ""; };
  return (
    <button data-testid={testid} className="btn btn-ghost" onClick={go}
      style={{ fontSize: 11, padding: "4px 10px", color: "#2C2C2C", fontWeight: 600 }}>
      {label}
    </button>
  );
}

// ═══ Change 3 — Mention Email Dropdown ═════════════════════════════════════
export function MentionEmailDropdown({ value, onChange, placeholder = "Type or select email…" }) {
  const [list, setList] = useState([]);
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  useEffect(() => { j("/api/iv/r12/mentions/emails").then(d => setList(d.emails || [])).catch(() => {}); }, []);
  useEffect(() => { setQ(value || ""); }, [value]);
  const filtered = (list || []).filter(e =>
    !q || e.email.toLowerCase().includes(q.toLowerCase()) || (e.name || "").toLowerCase().includes(q.toLowerCase())
  ).slice(0, 12);
  return (
    <div style={{ position: "relative" }}>
      <input data-testid="mention-email-input" type="text" value={q} placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={e => { setQ(e.target.value); setOpen(true); onChange && onChange(e.target.value); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={{ width: "100%", padding: "6px 10px", border: "1px solid var(--border)", borderRadius: 4, fontSize: 12, color: "#2C2C2C", background: "var(--bg)", fontWeight: 500 }} />
      {open && filtered.length > 0 && (
        <div data-testid="mention-email-dropdown"
          style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 4, marginTop: 2, maxHeight: 240, overflowY: "auto", zIndex: 50, boxShadow: "0 4px 12px rgba(0,0,0,.15)" }}>
          {filtered.map(e => (
            <div key={e.email} data-testid={`mention-email-opt-${e.email}`}
              onMouseDown={() => { setQ(e.email); onChange && onChange(e.email); setOpen(false); }}
              style={{ padding: "6px 10px", cursor: "pointer", fontSize: 12, borderBottom: "1px solid var(--border)", color: "#2C2C2C" }}>
              <div style={{ fontWeight: 600 }}>{e.name}</div>
              <div style={{ fontSize: 10, color: "#555" }}>{e.email} · {e.kind}{e.subtitle ? ` · ${e.subtitle}` : ""}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══ Change 6 — Customer Popout from Jobs list ═════════════════════════════
export function CustomerPopout({ job, onClose }) {
  if (!job) return null;
  const items = [
    ["Customer", job.customer], ["Contact phone", job.phone || job.contact_phone],
    ["Contact email", job.email || job.contact_email], ["Property address", job.address],
    ["Job #", job.id || job.job_number], ["Type", job.type], ["Current stage", job.stage || job.status],
    ["Assigned tech", job.tech], ["Started", job.date_started || job.scheduledAt],
    ["Target completion", job.target_completion], ["Notes", job.notes || job.last_note],
    ["Outstanding", job.outstanding ? `$${(+job.outstanding).toLocaleString()}` : null],
  ].filter(([_, v]) => v);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" data-testid="customer-popout" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }}>
        <div className="modal-header">
          <div style={{ fontFamily: "var(--rajdhani)", fontSize: 16, fontWeight: 700, color: "#2C2C2C" }}>
            {job.customer || "Customer details"}
          </div>
          <BackButton onBack={onClose} label="← Close" testid="customer-popout-close" />
        </div>
        <div style={{ padding: 18 }}>
          {items.map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--border)", fontSize: 12 }}>
              <span style={{ color: "#2C2C2C", fontWeight: 600 }}>{k}</span>
              <span style={{ color: "var(--text)", textAlign: "right" }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══ Change 7 — Per-section percentage badges ═════════════════════════════
export function JobSectionPercentages({ jobId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!jobId) return;
    (async () => {
      try {
        const [comp, photo, claim, sig] = await Promise.all([
          j(`/api/iv/r11/compliance/${jobId}`).catch(() => ({ complete: 0, total: 0 })),
          j(`/api/iv/r11/photo-gate/${jobId}`).catch(() => ({ photo_count: 0, by_stage: {} })),
          j(`/api/iv/r11/claim/${jobId}`).catch(() => ({ claim: null })),
          j(`/api/iv/r11/sign/list?job_id=${jobId}`).catch(() => ({ signatures: [] })),
        ]);
        const compPct = comp.total ? Math.round((comp.complete / comp.total) * 100) : 0;
        const photoOk = Object.values(photo.by_stage || {}).filter(s => s.ok).length;
        const photoTotal = Object.keys(photo.by_stage || {}).length || 1;
        const photoPct = Math.round((photoOk / photoTotal) * 100);
        const sigs = sig.signatures || [];
        const sigDone = sigs.filter(s => s.status === "signed").length;
        const sigPct = sigs.length ? Math.round((sigDone / sigs.length) * 100) : 0;
        const c = claim.claim;
        const paymentPct = c ? Math.round((((c.acv_check_amount || 0) + (c.rcv_check_amount || 0)) / ((c.carrier_approved_amount || 1) + 0.0001)) * 100) : 0;
        setData([
          { key: "compliance", label: "Compliance", pct: compPct },
          { key: "photos", label: "Photos", pct: photoPct },
          { key: "documents", label: "Documents", pct: sigPct },
          { key: "payments", label: "Payments", pct: Math.min(100, paymentPct) },
        ]);
      } catch (e) { /* no-op */ }
    })();
  }, [jobId]);
  if (!data) return null;
  return (
    <div data-testid="job-section-percentages" style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
      {data.map(d => {
        const c = d.pct >= 80 ? "#16a34a" : d.pct >= 40 ? "#d97706" : "#dc2626";
        return (
          <span key={d.key} data-testid={`sec-pct-${d.key}`}
            style={{ background: c, color: "#fff", fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 3, letterSpacing: 0.5 }}>
            {d.label}: {d.pct}%
          </span>
        );
      })}
    </div>
  );
}

// ═══ Change 8 — Average Job Size Page ══════════════════════════════════════
export function AvgJobSizePage({ onBack }) {
  const [d, setD] = useState(null);
  useEffect(() => { j("/api/iv/r12/reports/avg_job_size").then(setD).catch(() => {}); }, []);
  const fmt = n => `$${Number(n || 0).toLocaleString()}`;
  return (
    <div data-testid="avg-job-size-page">
      <BackButton onBack={onBack} />
      <h2 style={{ ...subhead, marginTop: 10 }}>Average Job Size</h2>
      {!d ? <div>Loading…</div> : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 16 }}>
            <div style={card}><div style={labelDark}>OVERALL AVG</div><div style={{ fontSize: 28, fontFamily: "var(--rajdhani)", fontWeight: 700, color: "var(--cyan)" }}>{fmt(d.overall_avg)}</div></div>
            <div style={card}><div style={labelDark}>TOTAL JOBS</div><div style={{ fontSize: 28, fontFamily: "var(--rajdhani)", fontWeight: 700, color: "#2C2C2C" }}>{d.total_jobs}</div></div>
            <div style={card}><div style={labelDark}>TOTAL INVOICED</div><div style={{ fontSize: 28, fontFamily: "var(--rajdhani)", fontWeight: 700, color: "#2C2C2C" }}>{fmt(d.total_invoiced)}</div></div>
          </div>
          {[["By Job Type", d.by_type], ["By Tech", d.by_tech], ["By Referral Source", d.by_referral_source], ["By Month", d.by_month]].map(([title, rows]) => (
            <div key={title} style={card}>
              <div style={subhead}>{title}</div>
              <table style={{ width: "100%", fontSize: 12 }}>
                <thead><tr><th align="left" style={{ padding: 4, color: "#2C2C2C" }}>Group</th><th align="right" style={{ color: "#2C2C2C" }}>Jobs</th><th align="right" style={{ color: "#2C2C2C" }}>Avg</th><th align="right" style={{ color: "#2C2C2C" }}>Total</th></tr></thead>
                <tbody>
                  {(rows || []).map(r => (
                    <tr key={r.key} data-testid={`avg-row-${title.replace(/\s+/g, "_")}-${r.key}`}>
                      <td style={{ padding: 4 }}>{r.key}</td>
                      <td align="right">{r.count}</td>
                      <td align="right">{fmt(r.avg)}</td>
                      <td align="right">{fmt(r.total)}</td>
                    </tr>
                  ))}
                  {!(rows || []).length && <tr><td colSpan={4} style={{ textAlign: "center", color: "#94a3b8", padding: 10 }}>No data yet.</td></tr>}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ═══ Change 10 — Total Invoices Card ═══════════════════════════════════════
export function TotalInvoicesCard({ onOpen }) {
  const [d, setD] = useState(null);
  useEffect(() => { j("/api/iv/r12/reports/total_invoices").then(setD).catch(() => {}); }, []);
  const fmt = n => `$${Number(n || 0).toLocaleString()}`;
  return (
    <div data-testid="total-invoices-card" onClick={onOpen} style={{ ...card, cursor: onOpen ? "pointer" : "default" }}>
      <div style={subhead}>Total Invoices</div>
      {!d ? <div>Loading…</div> : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, fontSize: 12 }}>
          {[["Total", d.total_invoices, "#2C2C2C"], ["Paid", d.paid, "#16a34a"], ["Outstanding", d.outstanding, "#d97706"], ["Overdue", d.overdue, "#dc2626"]].map(([lbl, v, c]) => (
            <div key={lbl} data-testid={`ti-${lbl.toLowerCase()}`}>
              <div style={{ fontSize: 10, color: "#2C2C2C", fontWeight: 600, textTransform: "uppercase" }}>{lbl}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: c, fontFamily: "var(--rajdhani)" }}>{v.count}</div>
              <div style={{ fontSize: 11, color: "#2C2C2C", fontWeight: 600 }}>{fmt(v.value)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══ Change 11 — P&L Page ══════════════════════════════════════════════════
export function PnLPage({ onBack }) {
  const [d, setD] = useState(null);
  const [period, setPeriod] = useState("month");
  const [oh, setOh] = useState({ monthly_overhead: 0, labor_rate_per_hour: 35, equipment_cost_pct_of_revenue: 5, notes: "" });
  const load = useCallback(() => {
    j(`/api/iv/r12/reports/pnl?period=${period}`).then(d => { setD(d); setOh(d.overhead_config || oh); }).catch(() => {});
  }, [period]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  const fmt = n => `$${Number(n || 0).toLocaleString()}`;
  const trend = (k) => {
    const t = (d?.trends || {})[k];
    if (t == null) return null;
    return <span style={{ marginLeft: 6, fontSize: 11, color: t >= 0 ? "#16a34a" : "#dc2626", fontWeight: 700 }}>{t >= 0 ? "▲" : "▼"} {Math.abs(t)}%</span>;
  };
  const saveOh = async () => {
    await j("/api/iv/r12/pnl/overhead", { method: "PUT", body: JSON.stringify(oh) });
    load();
  };
  if (!d) return <div>Loading P&L…</div>;
  const c = d.current;
  return (
    <div data-testid="pnl-page">
      <BackButton onBack={onBack} />
      <div style={{ display: "flex", gap: 6, margin: "10px 0", alignItems: "center" }}>
        <h2 style={subhead}>Profit & Loss</h2>
        <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
          {["month", "quarter", "year"].map(p => (
            <button key={p} data-testid={`pnl-period-${p}`} onClick={() => setPeriod(p)}
              className={period === p ? "btn btn-primary" : "btn btn-ghost"} style={{ fontSize: 11 }}>{p}</button>
          ))}
        </div>
      </div>
      <div style={card} data-testid="pnl-summary">
        {[
          ["Revenue (collected)", c.revenue, "revenue"],
          ["Cost of Labor", -c.labor_cost, "labor_cost"],
          ["Cost of Materials", -c.materials_cost, "materials_cost"],
          ["Cost of Equipment", -c.equipment_cost],
          ["Overhead", -c.overhead],
          ["Gross Profit", c.gross_profit, "gross_profit"],
          ["Net Profit", c.net_profit, "net_profit"],
        ].map(([lbl, v, tk]) => (
          <div key={lbl} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--border)", fontSize: 13 }}>
            <span style={{ color: "#2C2C2C", fontWeight: 600 }}>{lbl}</span>
            <span style={{ color: v < 0 ? "#dc2626" : "#16a34a", fontWeight: 700, fontFamily: "var(--mono)" }}>
              {fmt(v)}{tk && trend(tk)}
            </span>
          </div>
        ))}
        <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0 0", fontSize: 12 }}>
          <span style={{ color: "#2C2C2C", fontWeight: 600 }}>Gross margin</span>
          <span style={{ color: "#2C2C2C", fontWeight: 700 }}>{c.gross_margin_pct}%</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", fontSize: 12 }}>
          <span style={{ color: "#2C2C2C", fontWeight: 600 }}>Net margin</span>
          <span style={{ color: "#2C2C2C", fontWeight: 700 }}>{c.net_margin_pct}%</span>
        </div>
      </div>
      <div style={card}>
        <div style={subhead}>Owner — Overhead & Cost Config</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
          <div><label style={labelDark}>Monthly Overhead ($)</label><input data-testid="pnl-oh-monthly" type="number" value={oh.monthly_overhead} onChange={e => setOh({ ...oh, monthly_overhead: parseFloat(e.target.value) || 0 })} style={{ width: "100%", padding: 6, fontSize: 12, color: "#2C2C2C", fontWeight: 500 }} /></div>
          <div><label style={labelDark}>Labor $/hr</label><input data-testid="pnl-oh-labor" type="number" value={oh.labor_rate_per_hour} onChange={e => setOh({ ...oh, labor_rate_per_hour: parseFloat(e.target.value) || 0 })} style={{ width: "100%", padding: 6, fontSize: 12, color: "#2C2C2C", fontWeight: 500 }} /></div>
          <div><label style={labelDark}>Equipment % of revenue</label><input data-testid="pnl-oh-equipment" type="number" value={oh.equipment_cost_pct_of_revenue} onChange={e => setOh({ ...oh, equipment_cost_pct_of_revenue: parseFloat(e.target.value) || 0 })} style={{ width: "100%", padding: 6, fontSize: 12, color: "#2C2C2C", fontWeight: 500 }} /></div>
        </div>
        <button data-testid="pnl-oh-save" onClick={saveOh} className="btn btn-primary" style={{ marginTop: 10, fontSize: 11 }}>Save & Recalculate</button>
      </div>
    </div>
  );
}

// ═══ Change 12 — Special Permissions ════════════════════════════════════════
export function SpecialPermissionsPage({ onBack }) {
  const [catalog, setCatalog] = useState([]);
  const [grants, setGrants] = useState([]);
  const [team, setTeam] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ user_email: "", permission_keys: [], note: "", apply_to_arm: true, apply_to_fieldproiq: true });
  const load = () => {
    j("/api/iv/r12/perms/catalog").then(d => setCatalog(d.permissions || [])).catch(() => {});
    j("/api/iv/r12/perms/list").then(d => setGrants(d.grants || [])).catch(() => {});
    j("/api/iv/team").then(d => setTeam(d.team || [])).catch(() => {});
  };
  useEffect(load, []);
  const save = async () => {
    if (!form.user_email || form.permission_keys.length === 0) { alert("Pick a user and at least one permission."); return; }
    try { await j("/api/iv/r12/perms/grant", { method: "PUT", body: JSON.stringify(form) }); setShowAdd(false); setForm({ user_email: "", permission_keys: [], note: "", apply_to_arm: true, apply_to_fieldproiq: true }); load(); }
    catch (e) { alert(e.message); }
  };
  const revoke = async (email) => { if (!window.confirm(`Revoke all special permissions for ${email}?`)) return; await j(`/api/iv/r12/perms/${encodeURIComponent(email)}`, { method: "DELETE" }); load(); };
  return (
    <div data-testid="special-perms-page">
      <BackButton onBack={onBack} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
        <h2 style={subhead}>Special Permissions (Owner only)</h2>
        <button data-testid="special-perms-add" onClick={() => setShowAdd(true)} className="btn btn-primary" style={{ fontSize: 11 }}>+ Grant Permission</button>
      </div>
      <div style={card}>
        <div style={{ fontSize: 12, color: "#2C2C2C", marginBottom: 10, fontWeight: 500 }}>
          Grants override role defaults for specific users. They apply across <strong>Intelligence + ARM + FieldProIQ</strong>.
        </div>
        <table style={{ width: "100%", fontSize: 12 }}>
          <thead><tr><th align="left" style={{ color: "#2C2C2C", padding: 4 }}>User</th><th align="left" style={{ color: "#2C2C2C" }}>Permissions</th><th align="center" style={{ color: "#2C2C2C" }}>ARM</th><th align="center" style={{ color: "#2C2C2C" }}>FieldProIQ</th><th></th></tr></thead>
          <tbody>
            {grants.map(g => (
              <tr key={g.user_email} data-testid={`special-perms-row-${g.user_email}`}>
                <td style={{ padding: 4 }}>{g.user_email}</td>
                <td>{(g.permission_keys || []).map(k => <span key={k} style={{ background: "var(--cyan)", color: "#fff", padding: "1px 6px", borderRadius: 3, marginRight: 3, fontSize: 10 }}>{k}</span>)}</td>
                <td align="center">{g.apply_to_arm ? "✓" : "—"}</td>
                <td align="center">{g.apply_to_fieldproiq ? "✓" : "—"}</td>
                <td><button onClick={() => revoke(g.user_email)} className="btn btn-ghost" data-testid={`special-perms-revoke-${g.user_email}`} style={{ fontSize: 10, color: "#dc2626" }}>Revoke</button></td>
              </tr>
            ))}
            {!grants.length && <tr><td colSpan={5} style={{ textAlign: "center", padding: 12, color: "#94a3b8" }}>No grants yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" data-testid="special-perms-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header"><div style={{ color: "#2C2C2C", fontWeight: 700, fontFamily: "var(--rajdhani)", fontSize: 16 }}>Grant Special Permission</div><button className="icon-btn" onClick={() => setShowAdd(false)}>×</button></div>
            <div style={{ padding: 18 }}>
              <label style={labelDark}>User</label>
              <select data-testid="special-perms-user" value={form.user_email} onChange={e => setForm({ ...form, user_email: e.target.value })} style={{ width: "100%", padding: 6, fontSize: 12, color: "#2C2C2C", fontWeight: 600 }}>
                <option value="">— Pick a team member —</option>
                {team.map(m => <option key={m.id} value={m.email}>{m.full_name || m.email} · {m.system_role}</option>)}
              </select>
              <label style={{ ...labelDark, marginTop: 14 }}>Grant access to:</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, maxHeight: 250, overflowY: "auto", border: "1px solid var(--border)", padding: 8, borderRadius: 4 }}>
                {catalog.map(k => (
                  <label key={k} data-testid={`special-perms-perm-${k}`} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#2C2C2C", fontWeight: 500 }}>
                    <input type="checkbox" checked={form.permission_keys.includes(k)}
                      onChange={e => setForm({ ...form, permission_keys: e.target.checked ? [...form.permission_keys, k] : form.permission_keys.filter(x => x !== k) })} />
                    {k.replace(/_/g, " ")}
                  </label>
                ))}
              </div>
              <div style={{ display: "flex", gap: 14, marginTop: 12, fontSize: 12, color: "#2C2C2C", fontWeight: 500 }}>
                <label><input type="checkbox" checked={form.apply_to_arm} onChange={e => setForm({ ...form, apply_to_arm: e.target.checked })} data-testid="special-perms-arm" /> Apply to ARM</label>
                <label><input type="checkbox" checked={form.apply_to_fieldproiq} onChange={e => setForm({ ...form, apply_to_fieldproiq: e.target.checked })} data-testid="special-perms-fpiq" /> Apply to FieldProIQ</label>
              </div>
              <label style={{ ...labelDark, marginTop: 12 }}>Note (audit log)</label>
              <textarea rows={2} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} style={{ width: "100%", padding: 6, fontSize: 12, color: "#2C2C2C" }} />
              <button onClick={save} className="btn btn-primary" data-testid="special-perms-save" style={{ marginTop: 14, fontSize: 12 }}>Grant & Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
